export type TableSize = 'small' | 'medium' | 'large'
export type EstimatedHoursFilter = 'all' | 'has' | 'none'
export type SortField = 'createdAt' | 'title' | 'priority' | 'status'
export type SortDirection = 'asc' | 'desc'

export const UNASSIGNED_ASSIGNEE = '__unassigned__'
export const NO_SOURCE = '__none__'
export const PRIORITY_OPTIONS = ['untracked', 'low', 'normal', 'high', 'urgent'] as const

export const PRIORITY_RANK: Record<string, number> = {
  untracked: 0,
  low: 1,
  normal: 2,
  high: 3,
  urgent: 4,
}

export interface TicketFilterState {
  selectedStatuses: string[]
  selectedPriorities: string[]
  selectedDepartments: string[]
  selectedSources: string[]
  selectedAssignees: string[]
  selectedClientOrgs: string[]
  selectedClientUsers: string[]
  tagSearch: string
  createdFrom: string
  createdTo: string
  estimatedHoursFilter: EstimatedHoursFilter
  tableSize: TableSize
  sortField: SortField
  sortDirection: SortDirection
  showActiveFilterPills: boolean
}

interface TicketLike {
  id: string
  title: string
  ticketNumber: string
  status: { id: string; name: string }
  priority: { name: string }
  department: { id: string; name: string }
  source?: { id: string; name: string } | null
  clientOrg?: { id: string; name: string }
  clientUser?: { id: string }
  assignedUser?: { id: string }
  tags?: string[]
  estimatedHours?: number
  createdAt: string
}

export function sanitizeSelectedIds(selected: string[], validIds: Set<string>): string[] {
  const sanitized = selected.filter(id => validIds.has(id))
  if (
    sanitized.length === selected.length &&
    sanitized.every((id, index) => id === selected[index])
  ) {
    return selected
  }
  return sanitized
}

export function sanitizePrioritySelections(selected: string[]): string[] {
  const valid = new Set<string>(PRIORITY_OPTIONS)
  const sanitized = selected.filter(p => valid.has(p.toLowerCase()))
  if (
    sanitized.length === selected.length &&
    sanitized.every((id, index) => id === selected[index])
  ) {
    return selected
  }
  return sanitized
}

export function getDefaultStatusIds(
  statuses: Array<{ id: string; name: string; key?: string | null }>
): string[] {
  const excluded = new Set(['pending_closure', 'closed_complete'])
  return statuses
    .filter(s => {
      const nameLc = (s.name || '').toLowerCase()
      const keyLc = (s.key || '').toLowerCase()
      return !excluded.has(nameLc) && !excluded.has(keyLc)
    })
    .map(s => s.id)
}

/** Reconcile saved status IDs with current API data (e.g. after a local DB reset). */
export function resolveInitialStatusSelection(
  savedStatuses: string[],
  availableStatuses: Array<{ id: string; name: string; key?: string | null }>
): string[] {
  const validStatusIds = new Set(availableStatuses.map(s => s.id))
  const defaults = getDefaultStatusIds(availableStatuses)
  const sanitized = sanitizeSelectedIds(savedStatuses, validStatusIds)

  if (savedStatuses.length === availableStatuses.length) {
    return defaults
  }
  if (sanitized.length > 0) {
    return sanitized
  }
  if (savedStatuses.length > 0) {
    return defaults
  }
  return defaults
}

export function filterTickets<T extends TicketLike>(
  tickets: T[],
  filters: TicketFilterState,
  options?: { ticketSearch?: string; assignedToMe?: boolean; userId?: string }
): T[] {
  const search = options?.ticketSearch?.trim().toLowerCase() || ''

  return tickets.filter(ticket => {
    if (options?.assignedToMe && options.userId) {
      if (!ticket.assignedUser || ticket.assignedUser.id !== options.userId) {
        return false
      }
    }

    if (filters.selectedStatuses.length && !filters.selectedStatuses.includes(ticket.status.id)) {
      return false
    }
    if (
      filters.selectedPriorities.length &&
      !filters.selectedPriorities.includes(ticket.priority.name.toLowerCase())
    ) {
      return false
    }
    if (filters.selectedDepartments.length && !filters.selectedDepartments.includes(ticket.department.id)) {
      return false
    }
    if (filters.selectedSources.length) {
      const sourceId = ticket.source?.id || NO_SOURCE
      if (!filters.selectedSources.includes(sourceId)) return false
    }
    if (filters.selectedAssignees.length) {
      const assigneeId = ticket.assignedUser?.id || UNASSIGNED_ASSIGNEE
      if (!filters.selectedAssignees.includes(assigneeId)) return false
    }
    if (filters.selectedClientOrgs.length && !filters.selectedClientOrgs.includes(ticket.clientOrg?.id || '')) {
      return false
    }
    if (filters.selectedClientUsers.length && !filters.selectedClientUsers.includes(ticket.clientUser?.id || '')) {
      return false
    }

    if (search) {
      const inTitle = ticket.title.toLowerCase().includes(search)
      const inNumber = ticket.ticketNumber.toLowerCase().includes(search)
      if (!inTitle && !inNumber) return false
    }

    if (filters.tagSearch.trim()) {
      const q = filters.tagSearch.trim().toLowerCase()
      const tags = (ticket.tags || []).map(t => t.toLowerCase())
      if (!tags.some(t => t.includes(q))) return false
    }

    if (filters.createdFrom) {
      const from = new Date(filters.createdFrom)
      from.setHours(0, 0, 0, 0)
      if (new Date(ticket.createdAt) < from) return false
    }
    if (filters.createdTo) {
      const to = new Date(filters.createdTo)
      to.setHours(23, 59, 59, 999)
      if (new Date(ticket.createdAt) > to) return false
    }

    if (filters.estimatedHoursFilter === 'has' && !ticket.estimatedHours) return false
    if (filters.estimatedHoursFilter === 'none' && ticket.estimatedHours) return false

    return true
  })
}

export function sortTickets<T extends TicketLike>(
  tickets: T[],
  sortField: SortField,
  sortDirection: SortDirection
): T[] {
  const dir = sortDirection === 'asc' ? 1 : -1

  return [...tickets].sort((a, b) => {
    let cmp = 0
    switch (sortField) {
      case 'title':
        cmp = a.title.localeCompare(b.title)
        break
      case 'status':
        cmp = a.status.name.localeCompare(b.status.name)
        break
      case 'priority':
        cmp =
          (PRIORITY_RANK[a.priority.name.toLowerCase()] ?? 0) -
          (PRIORITY_RANK[b.priority.name.toLowerCase()] ?? 0)
        break
      case 'createdAt':
      default:
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        break
    }
    return cmp * dir
  })
}

export function countActiveFilters(filters: TicketFilterState): number {
  let count = 0
  if (filters.selectedStatuses.length) count++
  if (filters.selectedPriorities.length) count++
  if (filters.selectedDepartments.length) count++
  if (filters.selectedSources.length) count++
  if (filters.selectedAssignees.length) count++
  if (filters.selectedClientOrgs.length) count++
  if (filters.selectedClientUsers.length) count++
  if (filters.tagSearch.trim()) count++
  if (filters.createdFrom || filters.createdTo) count++
  if (filters.estimatedHoursFilter !== 'all') count++
  if (filters.sortField !== 'createdAt' || filters.sortDirection !== 'desc') count++
  if (filters.tableSize !== 'medium') count++
  return count
}

export function categoryActiveCount(
  categoryId: string,
  filters: TicketFilterState,
  isStaff: boolean
): number {
  switch (categoryId) {
    case 'workflow':
      return filters.selectedStatuses.length + filters.selectedPriorities.length
    case 'ticket':
      return (
        filters.selectedDepartments.length +
        filters.selectedSources.length +
        (filters.tagSearch.trim() ? 1 : 0) +
        (filters.estimatedHoursFilter !== 'all' ? 1 : 0)
      )
    case 'dates':
      return filters.createdFrom || filters.createdTo ? 1 : 0
    case 'people':
      if (!isStaff) return 0
      return (
        filters.selectedAssignees.length +
        filters.selectedClientOrgs.length +
        filters.selectedClientUsers.length
      )
    case 'display':
      return (
        (filters.sortField !== 'createdAt' || filters.sortDirection !== 'desc' ? 1 : 0) +
        (filters.tableSize !== 'medium' ? 1 : 0)
      )
    default:
      return 0
  }
}

export type FilterPillRemove =
  | { type: 'status'; value: string }
  | { type: 'priority'; value: string }
  | { type: 'department'; value: string }
  | { type: 'source'; value: string }
  | { type: 'assignee'; value: string }
  | { type: 'clientOrg'; value: string }
  | { type: 'clientUser'; value: string }
  | { type: 'tagSearch' }
  | { type: 'createdRange' }
  | { type: 'estimatedHours' }
  | { type: 'sort' }
  | { type: 'tableSize' }
  | { type: 'ticketSearch' }

export interface ActiveFilterPill {
  id: string
  label: string
  remove: FilterPillRemove
}

export interface FilterLabelLookup {
  status: Record<string, string>
  priority: Record<string, string>
  department: Record<string, string>
  source: Record<string, string>
  assignee: Record<string, string>
  clientOrg: Record<string, string>
  clientUser: Record<string, string>
}

function formatStatusLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

function sortPillLabel(sortField: SortField, sortDirection: SortDirection): string {
  const fieldLabels: Record<SortField, string> = {
    createdAt: 'Created',
    title: 'Title',
    priority: 'Priority',
    status: 'Status',
  }
  const directionLabel =
    sortField === 'title' || sortField === 'status'
      ? sortDirection === 'asc'
        ? 'A → Z'
        : 'Z → A'
      : sortDirection === 'asc'
        ? 'Oldest first'
        : 'Newest first'
  return `Sort: ${fieldLabels[sortField]} (${directionLabel})`
}

export function buildActiveFilterPills(
  filters: TicketFilterState,
  labels: FilterLabelLookup,
  options?: { ticketSearch?: string }
): ActiveFilterPill[] {
  const pills: ActiveFilterPill[] = []

  if (options?.ticketSearch?.trim()) {
    pills.push({
      id: 'ticket-search',
      label: `Search: ${options.ticketSearch.trim()}`,
      remove: { type: 'ticketSearch' },
    })
  }

  for (const id of filters.selectedStatuses) {
    pills.push({
      id: `status-${id}`,
      label: labels.status[id] || id,
      remove: { type: 'status', value: id },
    })
  }

  for (const id of filters.selectedPriorities) {
    const name = labels.priority[id] || id
    pills.push({
      id: `priority-${id}`,
      label: name.charAt(0).toUpperCase() + name.slice(1),
      remove: { type: 'priority', value: id },
    })
  }

  for (const id of filters.selectedDepartments) {
    pills.push({
      id: `department-${id}`,
      label: labels.department[id] || id,
      remove: { type: 'department', value: id },
    })
  }

  for (const id of filters.selectedSources) {
    pills.push({
      id: `source-${id}`,
      label: labels.source[id] || id,
      remove: { type: 'source', value: id },
    })
  }

  for (const id of filters.selectedAssignees) {
    pills.push({
      id: `assignee-${id}`,
      label: labels.assignee[id] || id,
      remove: { type: 'assignee', value: id },
    })
  }

  for (const id of filters.selectedClientOrgs) {
    pills.push({
      id: `org-${id}`,
      label: labels.clientOrg[id] || id,
      remove: { type: 'clientOrg', value: id },
    })
  }

  for (const id of filters.selectedClientUsers) {
    pills.push({
      id: `user-${id}`,
      label: labels.clientUser[id] || id,
      remove: { type: 'clientUser', value: id },
    })
  }

  if (filters.tagSearch.trim()) {
    pills.push({
      id: 'tag-search',
      label: `Tag: ${filters.tagSearch.trim()}`,
      remove: { type: 'tagSearch' },
    })
  }

  if (filters.createdFrom || filters.createdTo) {
    let label = 'Created: '
    if (filters.createdFrom && filters.createdTo) {
      label += `${filters.createdFrom} → ${filters.createdTo}`
    } else if (filters.createdFrom) {
      label += `from ${filters.createdFrom}`
    } else {
      label += `until ${filters.createdTo}`
    }
    pills.push({
      id: 'created-range',
      label,
      remove: { type: 'createdRange' },
    })
  }

  if (filters.estimatedHoursFilter === 'has') {
    pills.push({
      id: 'estimated-hours',
      label: 'Has estimate',
      remove: { type: 'estimatedHours' },
    })
  } else if (filters.estimatedHoursFilter === 'none') {
    pills.push({
      id: 'estimated-hours',
      label: 'No estimate',
      remove: { type: 'estimatedHours' },
    })
  }

  if (filters.sortField !== 'createdAt' || filters.sortDirection !== 'desc') {
    pills.push({
      id: 'sort',
      label: sortPillLabel(filters.sortField, filters.sortDirection),
      remove: { type: 'sort' },
    })
  }

  if (filters.tableSize !== 'medium') {
    pills.push({
      id: 'table-size',
      label: `Table: ${filters.tableSize.charAt(0).toUpperCase()}${filters.tableSize.slice(1)}`,
      remove: { type: 'tableSize' },
    })
  }

  return pills
}

export { formatStatusLabel }
